import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { Col, Form, Input, Row, Modal, Skeleton, message } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import PermissionsCard, {
  GroupedRolesProps,
  RoleProps,
} from '@components/PermissionsCard';
import api from '@services/api';
import { permissionsThatBusinessCannotPassOn } from '@utils/permissionsThatBusinessCannotPassOn';
import rolesConstants from '@utils/rolesConstants';

const { Item } = Form;

interface IGroupViewModalProps {
  type: 'MINE' | 'CUSTOMERS';
  groupId: string;
  visible: boolean;
  onClose: () => void;
  mutateGroups: (type: 'CREATE' | 'DELETE', data: any) => void;
  selectedCustomerId?: number;
  selectedCustomerName?: string;
}

const GroupViewerModal: React.FC<IGroupViewModalProps> = ({
  type,
  groupId,
  visible,
  onClose,
  mutateGroups,
  selectedCustomerId,
  selectedCustomerName,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['roles', 'permissions', 'attributes']);
  const {
    isMotBusiness,
    mainCustomerInContext,
    isNotChildOfRoot,
    customer: { getCustomerById, customerLogged },
  } = useContext(SessionContext);
  const isNewGroup = groupId === '';

  const [modalVisible, setModalVisible] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const { data: groupToUpdate } = useSwr(
    groupId !== '' ? `/groups/${groupId}` : null,
  );

  const isGroupToUpdateAdmin = useMemo(
    () => groupToUpdate?.name === 'ADMINISTRATORS',
    [groupToUpdate?.name],
  );

  const customerId = useMemo(() => {
    if (isNewGroup) {
      return type === 'CUSTOMERS' ? selectedCustomerId : customerLogged?.id;
    }
    const idToAdminGroup =
      type === 'CUSTOMERS' ? customerLogged?.id : mainCustomerInContext?.id;
    return isGroupToUpdateAdmin ? idToAdminGroup : groupToUpdate?.customerId;
  }, [
    customerLogged?.id,
    groupToUpdate?.customerId,
    isGroupToUpdateAdmin,
    isNewGroup,
    mainCustomerInContext?.id,
    selectedCustomerId,
    type,
  ]);

  const customerType = useMemo(() => {
    if (customerLogged?.type !== 'CUSTOMER') {
      const idToGetType = isNewGroup
        ? selectedCustomerId
        : groupToUpdate?.customerId;
      return type === 'CUSTOMERS'
        ? getCustomerById(idToGetType)?.type
        : customerLogged?.type;
    }
    return 'CUSTOMER';
  }, [
    customerLogged?.type,
    getCustomerById,
    groupToUpdate?.customerId,
    isNewGroup,
    selectedCustomerId,
    type,
  ]);

  const isChildOfBusiness = useMemo(
    () =>
      isMotBusiness &&
      getCustomerById(isNewGroup ? customerId : groupToUpdate?.customerId),
    [
      customerId,
      getCustomerById,
      groupToUpdate?.customerId,
      isMotBusiness,
      isNewGroup,
    ],
  );

  const { data: availableRoles } = useSwr<RoleProps[]>(
    customerId ? `/customers/${customerId}/roles/` : null,
    {
      params: {
        customerType,
      },
    },
  );

  const loading = useMemo(
    () => !availableRoles || (!isNewGroup && !groupToUpdate),
    [availableRoles, groupToUpdate, isNewGroup],
  );

  const updateGroupRoles = useCallback(
    (id: string, rolesToPut: any) =>
      api.put(`/groups/${id}/roles/`, rolesToPut),
    [],
  );

  const formatGroupedRoles = useCallback(
    (roles: RoleProps[] | undefined) =>
      roles?.reduce(
        (groups: any, item: any) => ({
          ...groups,
          [item.category]: [...(groups[item.category] || []), item],
        }),
        {},
      ),
    [],
  );

  const filteredRoles = useMemo(
    (): RoleProps[] | undefined =>
      isChildOfBusiness
        ? availableRoles?.filter(
            role =>
              !permissionsThatBusinessCannotPassOn.includes(role.name) && role,
          )
        : availableRoles,
    [availableRoles, isChildOfBusiness],
  );

  const groupedRoles = useMemo((): GroupedRolesProps[] | undefined => {
    const groupedBy = formatGroupedRoles(filteredRoles);

    return groupedBy ? Object.entries(groupedBy) : undefined;
  }, [formatGroupedRoles, filteredRoles]);

  const formInitialValues = useMemo(() => {
    if (!isNewGroup && groupToUpdate) {
      const fields = { ...groupToUpdate };
      fields.roles = fields?.roles?.map((r: any) => r.id);
      return fields;
    }

    const fields: any = {};
    fields.customerId = customerId;
    return fields;
  }, [customerId, groupToUpdate, isNewGroup]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(formInitialValues);
    setModalVisible(visible);
  }, [form, formInitialValues, visible]);

  const close = useCallback(() => {
    setModalVisible(false);
    setSelectedRoles([]);
    form.resetFields();
    onClose();
  }, [form, onClose]);

  // clear permissions error
  useEffect(() => {
    if (selectedRoles?.length > 0) {
      form.setFields([
        {
          name: 'roles',
          errors: [],
        },
      ]);
    }
  }, [selectedRoles, form]);

  const onFinish = useCallback(
    async (values: { [x: string]: any; roles: any }) => {
      if (selectedRoles?.length === 0) {
        form.setFields([
          {
            name: 'roles',
            errors: [t('modal.form.msgs.chooseAtLeastOne')],
          },
        ]);
        return;
      }
      const newRoles = selectedRoles.map(r => ({ id: r }));

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { roles, ...group } = values;

      setSaveLoading(true);
      if (isNewGroup) {
        try {
          const { data } = await api.post('/groups/', {
            ...group,
            customerId,
          });

          await updateGroupRoles(data.id, newRoles);
          const { data: groupData } = await api.get(`/groups/${data.id}`);
          mutateGroups('CREATE', groupData);
          message.success(t('msgs.success-creating-profile'));
          close();
          // @ts-ignore
        } catch ({ response }) {
          // @ts-ignore
          if (response?.status === 409) {
            form.setFields([
              {
                name: 'prettyName',
                errors: [t('modal.form.msgs.profileAlreadyRegistered')],
              },
            ]);
          } else {
            message.error(t('msgs.error-creating-profile'));
          }
        }
      } else {
        try {
          await updateGroupRoles(groupId, newRoles);

          if (
            isGroupToUpdateAdmin &&
            customerLogged?.id === groupToUpdate?.customerId
          ) {
            setTimeout(() => {
              window.location.reload();
            }, 800);
          }
          message.success(t('msgs.success-updating-profile'));
          close();
        } catch {
          message.error(t('msgs.error-updating-profile'));
        }
      }
      setSaveLoading(false);
    },
    [
      selectedRoles,
      isNewGroup,
      form,
      t,
      customerId,
      updateGroupRoles,
      mutateGroups,
      close,
      groupId,
      isGroupToUpdateAdmin,
      customerLogged?.id,
      groupToUpdate?.customerId,
    ],
  );

  const prettyNameItem = useMemo(
    () => (
      <Item
        label={t('modal.form.labels.name')}
        name="prettyName"
        rules={[
          {
            required: isNewGroup,
            message: t('modal.form.msgs.fillThisField'),
          },
        ]}
      >
        <Input disabled={!isNewGroup} />
      </Item>
    ),
    [isNewGroup, t],
  );

  return (
    <Modal
      cancelText={t('modal.buttons.cancelButton')}
      confirmLoading={saveLoading}
      maskClosable={false}
      okButtonProps={{
        form: 'groupForm',
        htmlType: 'submit',
      }}
      okText={t('modal.buttons.saveButton')}
      title={isNewGroup ? t('modal.titles.newRole') : groupToUpdate?.prettyName}
      open={modalVisible}
      width="100%"
      style={{ maxWidth: 760 }}
      centered
      afterClose={() => document.body.style.removeProperty('overflow')}
      onCancel={() => {
        close();
        setSaveLoading(false);
      }}
    >
      {selectedCustomerName && (
        <div style={{ textAlign: isNewGroup ? 'center' : 'left' }}>
          <p style={{ margin: 0 }}>
            {t(`attributes:${isMotBusiness ? 'department' : 'customer'}`)}
          </p>
          <strong style={{ fontSize: '1rem' }}>{selectedCustomerName}</strong>
        </div>
      )}
      <Form
        colon={false}
        form={form}
        id="groupForm"
        layout="vertical"
        scrollToFirstError
        onFinish={onFinish}
      >
        <Row gutter={16} style={{ padding: '8px 16px' }}>
          {isNewGroup && (
            <Col sm={24} xs={24} style={{ padding: '0 0 0 5px' }}>
              {prettyNameItem}
            </Col>
          )}
        </Row>

        {(customerId || !isNewGroup) && (
          <Skeleton
            loading={loading}
            title={false}
            active
            style={{ paddingTop: !isNewGroup ? 40 : 'auto' }}
          >
            <Item name="roles">
              <PermissionsCard
                groupedRoles={groupedRoles}
                selectedRoles={formInitialValues?.roles}
                setSelectedRoles={setSelectedRoles}
                filterRoles={[
                  {
                    category: 'support',
                    roles: [
                      {
                        name: rolesConstants.VIEW_SERVICE_REQUESTS,
                        visible: !isNotChildOfRoot,
                      },
                      {
                        name: rolesConstants.MANAGE_SERVICE_REQUESTS,
                        visible: !isNotChildOfRoot,
                      },
                    ],
                  },
                ]}
              />
            </Item>
          </Skeleton>
        )}
      </Form>
    </Modal>
  );
};

export default GroupViewerModal;
